import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import type { FormRenderProps } from "@tql/ui-components-react/kendo/form";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { Form } from "@tql/ui-components-react/tql/form/beta";
import { pencil_01 } from "@tql/ui-components-react/untitled/icons";
import { useAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBroker } from "~/features/ftl/ftl-header/hooks/use-broker.tsx";
import type { Stop } from "~/features/ftl/shared/ftl-stop";
import {
	createDefaultStops,
	toTimezoneSafeDate,
} from "~/features/ftl/shared/ftl-stop.ts";
import type { Step } from "~/features/quote/components/quoting-accordion/ftl-quoting-accordion.tsx";
import { FtlQuotingAccordion } from "~/features/quote/components/quoting-accordion/ftl-quoting-accordion.tsx";
import { YesNoDialog } from "~/features/quote/components/yes-no-dialog/yes-no-dialog.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useAutoSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { useNotifications } from "~/shared/hooks/use-notifications.ts";
import {
	sendFtlQuoteGetQuoteEvent,
	sendFtlQuoteReviseQuoteEvent,
} from "~/shared/utils/google-analytics-helper.ts";

import { activeFtlQuoteAtom } from "../atoms/active-ftl-quote.ts";
import { useCreateFtlQuote } from "../hooks/use-create-ftl-quote.tsx";
import { useFtlQuote } from "../hooks/use-ftl-quote.tsx";
import { useLoadValues } from "../hooks/use-load-values.tsx";
import { useTrailerTypes } from "../hooks/use-trailer-types.tsx";
import { QuoteDetailsStep } from "./components/quote-details-step/quote-details-step.tsx";
import { QuoteSummaryNuggets } from "./components/quote-summary-nuggets/quote-summary-nuggets.tsx";
import { NoRateMessage } from "./components/Rate-Tender-Details-step/no-rate-message/no-rate-message.tsx";
import { RateTenderDetailsStep } from "./components/Rate-Tender-Details-step/rate-tender-details-step.tsx";
import * as styles from "./create-ftl-quote.module.css";
import {
	validateFirstAndLastStopTypes,
	validateStopDatesChronological,
} from "./create-ftl-quote-validators.ts";

type LoadValue = {
	code: string;
	displayName: string;
};

type TrailerType = {
	code: string;
	displayName: string;
	isRefrigerated: boolean;
};

export type FTLQuoteRequestForm = {
	trailerType: TrailerType;
	temperature: string;
	loadValue: LoadValue;
	commodity: string;
	totalWeight: number;
	hazmat: boolean;
	customerPo?: string;
	specialInstructions?: string;
	stops: Stop[];
};

type CreateFTLQuoteStopCommand = {
	stopType: "PICK" | "DROP";
	postalCode: string;
	country: string;
	state: string;
	city: string;
	orderIndex: number;
	longitude: number;
	latitude: number;
	stopDate: Date;
};

export type CreateFTLQuoteCommand = {
	trailerType: string;
	trailerSize?: string;
	temperature: string;
	notifyQuoteCreation: boolean;
	loadValueCode: string;
	commodity: string;
	totalWeight: number;
	isHazmat: boolean;
	specialInstructions?: string;
	customerPo?: string;
	stops: CreateFTLQuoteStopCommand[];
	accessorials: string[];
};

export function mapFormToCreateQuoteCommand(
	quoteRequest: FTLQuoteRequestForm,
): CreateFTLQuoteCommand {
	const mappedStops = quoteRequest.stops.map((stop, index) => ({
		stopType: stop.stopType === "Pickup" ? "PICK" : "DROP",
		postalCode: stop.location?.postalCode,
		country: stop.location?.country,
		state: stop.location?.state,
		city: stop.location?.city,
		orderIndex: index + 1,
		longitude: stop.location?.longitude,
		latitude: stop.location?.latitude,
		stopDate: stop.stopDate ? toTimezoneSafeDate(stop.stopDate) : stop.stopDate,
	})) as unknown as CreateFTLQuoteCommand["stops"];

	return {
		totalWeight: quoteRequest.totalWeight,
		notifyQuoteCreation: true,
		isHazmat: quoteRequest.hazmat ?? false,
		temperature: quoteRequest.trailerType.isRefrigerated
			? quoteRequest.temperature
			: "",
		commodity: quoteRequest.commodity,
		trailerType: quoteRequest.trailerType.code,
		loadValueCode: quoteRequest.loadValue.code,
		specialInstructions: quoteRequest.specialInstructions || undefined,
		customerPo: quoteRequest.customerPo,
		stops: mappedStops,
		accessorials: [],
	};
}

// Module-level ref to access form state from StopsContainer
const formRef = { current: null as FormRenderProps | null };

export function CreateFtlQuote() {
	const MOBILE_BREAKPOINT = 992;
	const { trailerTypes, isLoading: isLoadingTrailerTypes } = useTrailerTypes();
	const { loadValues, isLoading: isLoadingLoadValues } = useLoadValues();
	const [rateAttempted, setRateAttempted] = useState<boolean>(false);
	const [rateReturned, setRateReturned] = useState<boolean>(false);
	const [quoteResultText, setQuoteResultText] = useState<string | null>(null);
	const [submittedFormData, setSubmittedFormData] =
		useState<FTLQuoteRequestForm | null>(null);
	const currentFormDataRef = useRef<FTLQuoteRequestForm | null>(null);
	// Mutation pending state only reaches the DOM after a re-render, so a second
	// submit dispatched before that commit would otherwise slip through.
	const inFlightRef = useRef(false);
	const { clearAllNotifications, showError } = useNotifications();
	const [, setActiveFtlQuote] = useAtom(activeFtlQuoteAtom);
	const [shouldShowReviseYesNo, setShouldShowReviseYesNo] = useState(false);
	const hostRef = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile(MOBILE_BREAKPOINT);
	const { isInternal } = useDeploymentType();

	const { broker } = useBroker("FTL");

	const {
		data: createdResponse,
		isPending: isCreatingQuote,
		createFtlQuote,
	} = useCreateFtlQuote({
		onSuccess: (data) => {
			sendFtlQuoteGetQuoteEvent(data.id.toString() ?? "");
			if (currentFormDataRef.current) {
				setSubmittedFormData(currentFormDataRef.current);
			}
		},
		onSettled: () => {
			inFlightRef.current = false;
		},
	});

	const { data: ftlQuoteData, isLoading: isLoadingFtlQuote } = useFtlQuote(
		createdResponse?.rate ? createdResponse.id : null,
	);

	const [tenderDialog, setTenderDialog] = useState<
		{ type: "success" } | { type: "error"; message: string } | null
	>(null);

	useEffect(() => {
		if (ftlQuoteData) {
			setActiveFtlQuote((previousQuote) => {
				if (previousQuote?.quoteId === ftlQuoteData.quoteId) {
					return previousQuote;
				}

				return ftlQuoteData;
			});
		}
	}, [ftlQuoteData, setActiveFtlQuote]);

	const showingSpinner = isCreatingQuote || isLoadingFtlQuote;
	useAutoSpinner(showingSpinner);

	useEffect(() => {
		if (createdResponse !== undefined) {
			if (createdResponse.rateAttempted) {
				setRateAttempted(true);
				if (createdResponse.rate === null) {
					if (!showingSpinner) {
						setQuoteResultText(
							"A rate was not able to generate via TRAX for this request. However a quote request has been sent to the assigned broker.",
						);
					}
				} else {
					setRateReturned(true);
				}
			} else {
				if (!showingSpinner) {
					setQuoteResultText(
						"The quote request has been sent to the assigned broker.",
					);
				}
			}
		}
	}, [createdResponse, showingSpinner, setQuoteResultText]);

	const initialFormValues = useMemo(
		() =>
			({
				stops: createDefaultStops(),
			}) as FTLQuoteRequestForm,
		[],
	);

	const handleSubmit = useCallback(
		(quoteRequest: FTLQuoteRequestForm) => {
			if (inFlightRef.current) return;

			const stopTypeError = validateFirstAndLastStopTypes(quoteRequest.stops);

			if (stopTypeError) {
				clearAllNotifications(); // To show only one error at a time.
				showError(stopTypeError, { closable: true });
				return; // Prevent submission
			}

			// Validate chronological order of stop dates
			const chronologicalError = validateStopDatesChronological(
				quoteRequest.stops,
			);

			if (chronologicalError) {
				clearAllNotifications(); // To show only one error at a time.
				showError(chronologicalError, { closable: true });
				return; // Prevent submission
			}

			// Reset rate state when submitting a new quote
			setRateReturned(false);

			// Store form data in ref for access in success callback
			currentFormDataRef.current = quoteRequest;

			inFlightRef.current = true;
			createFtlQuote(mapFormToCreateQuoteCommand(quoteRequest));
		},
		[clearAllNotifications, createFtlQuote, showError],
	);

	function reviseQuote() {
		setQuoteResultText(null);
		setRateReturned(false);
		setRateAttempted(false);
	}

	function submitAnotherQuote() {
		setTenderDialog(null);
		setActiveFtlQuote(null);
		setQuoteResultText(null);
		setRateReturned(false);
		setRateAttempted(false);
		setSubmittedFormData(null);
		currentFormDataRef.current = null;
		formRef.current?.onFormReset();
		formRef.current?.onChange("stops", { value: createDefaultStops() });
	}

	function closeQuoteResultModal() {
		setQuoteResultText(null);
	}

	const yesNoDialog = {
		show: (): void => setShouldShowReviseYesNo(true),
		hide: (): void => setShouldShowReviseYesNo(false),
		handleYes: (): void => {
			setShouldShowReviseYesNo(false);
			sendFtlQuoteReviseQuoteEvent();
			reviseQuote();
		},
		bodyText: "If you Revise, your current rate will be recalculated.",
		title: "Revise Quote?",
	};

	return (
		<div>
			<div
				ref={hostRef}
				className={styles.ftlQuotingContainer}
				data-testid={"create-ftl-quote"}
			>
				{isMobile ? null : (
					<TraxPageHeader
						representativeType={"ftl"}
						showSupportMessage={!isInternal}
						title={"FTL Quote"}
					/>
				)}
				<Form
					initialValues={initialFormValues}
					onReset={() => {
						setRateReturned(false);
						setSubmittedFormData(null);
					}}
					onSubmit={(values: FTLQuoteRequestForm) => handleSubmit(values)}
					render={(formRenderProps: FormRenderProps) => {
						// Store formRenderProps in ref for resetting after book
						formRef.current = formRenderProps;

						const steps: Step[] = [
							{
								step: 1,
								title: "Quote Details",
								id: "quote-details",
								active: !rateAttempted,
								completed: rateAttempted,
								expandCompleted: false,
								completedHeader:
									rateReturned && !!submittedFormData ? (
										<div className={"k-pl-5"}>
											<div className={styles.nuggetBar}>
												<QuoteSummaryNuggets
													formData={submittedFormData}
													quoteId={createdResponse?.id}
												/>
												<div className={"k-m-auto"}>
													<Button
														formAction={"none"}
														onClick={yesNoDialog.show}
														svgIcon={pencil_01}
														variant={"secondary"}
													>
														Revise
													</Button>
												</div>
											</div>
										</div>
									) : undefined,
								content: (
									<QuoteDetailsStep
										formRenderProps={formRenderProps}
										isLoadingLoadValues={isLoadingLoadValues}
										isLoadingTrailerTypes={isLoadingTrailerTypes}
										isSubmitting={showingSpinner}
										loadValues={loadValues}
										trailerTypes={trailerTypes}
									/>
								),
							},
						];

						if (rateAttempted) {
							steps.push({
								step: 2,
								title: "Rate & Tender Details",
								id: "rate-and-tender-details",
								active: rateAttempted,
								completed: rateAttempted,
								completedHeader:
									rateReturned && !!submittedFormData ? (
										<div className={"k-pl-5"}>
											<div className={styles.nuggetBar}></div>
										</div>
									) : undefined,
								content: rateReturned ? (
									<RateTenderDetailsStep
										onShipmentTendered={(result) =>
											setTenderDialog(
												result.success
													? { type: "success" }
													: {
															type: "error",
															message:
																result.message ??
																"We could not tender this quote right now. Please try again.",
														},
											)
										}
										rate={createdResponse?.rate}
										submittedFormData={submittedFormData}
										transitDays={createdResponse?.transitDays}
									/>
								) : (
									<NoRateMessage />
								),
							});
						}
						return <FtlQuotingAccordion steps={steps} />;
					}}
				/>
			</div>
			{quoteResultText == null ? undefined : (
				<TraxModal
					appendTo={hostRef.current}
					onClose={closeQuoteResultModal}
					title={"Thank you, your quote was submitted!"}
					width={"min(90vw, 450px)"}
				>
					{quoteResultText}
					<br /> <br />
					If you have any questions, please contact{" "}
					<span className={styles.noWrap}>{broker?.name || "your broker"}</span>
					{" at "}
					<span className={styles.noWrap}>
						(800) 580-3101 ext. {broker?.extension || "[unknown]"}
					</span>
					<DialogActionsBar layout={"end"}>
						<Button onClick={submitAnotherQuote} variant={"primary"}>
							Submit Another Quote
						</Button>
						<Button onClick={closeQuoteResultModal} variant={"secondary"}>
							Close
						</Button>
					</DialogActionsBar>
				</TraxModal>
			)}
			<YesNoDialog
				appendTo={hostRef.current}
				message={yesNoDialog.bodyText}
				noText={"Cancel"}
				onNo={yesNoDialog.hide}
				onYes={yesNoDialog.handleYes}
				open={shouldShowReviseYesNo}
				title={yesNoDialog.title}
				width={"min(90vw, 350px)"}
				yesText={"Yes. Revise Quote Details."}
			/>

			{tenderDialog?.type === "success" && (
				<TraxModal
					showConfirmationButtons
					appendTo={hostRef.current}
					confirmText={"Create Another Quote"}
					onClose={() => setTenderDialog(null)}
					onConfirm={() => {
						submitAnotherQuote();
					}}
					showCancelButton={false}
					title={"Shipment Tendered"}
					type={"success"}
				>
					<p>Your shipment was tendered successfully.</p>
				</TraxModal>
			)}

			{tenderDialog?.type === "error" && (
				<TraxModal
					showConfirmationButtons
					appendTo={hostRef.current}
					confirmText={"Edit Shipment"}
					onClose={() => setTenderDialog(null)}
					onConfirm={() => {
						setTenderDialog(null);
					}}
					showCancelButton={false}
					title={"Tender Failed"}
					type={"error"}
				>
					<p>{tenderDialog.message}</p>
				</TraxModal>
			)}
		</div>
	);
}
